import { useMemo, useEffect, useState, useCallback } from 'react';
import { Gym, FilterState } from '../types';
import { calculateHaversineDistance } from '../utils/geo';

export const GYMS_PER_PAGE = 12;

interface UseDirectoryResultsOptions {
    allGyms: Gym[];
    filters: FilterState;
    searchCenter: { lat: number; lng: number } | null;
    userLocation: { lat: number; lng: number } | null;
    sortBy: 'distance' | 'rating' | 'reviews';
    mapCenter: [number, number];
    /**
     * UI-72 — size of the FIRST page, defaulting to GYMS_PER_PAGE.
     *
     * WHY THIS EXISTS. `GYMS_PER_PAGE` is applied at PRERENDER as well as in the
     * browser, and "Load More" is client-only state that mints no url — so on a
     * suburb page holding more than 12 gyms, the ones ranked 13th+ were linked
     * from NOWHERE in the built corpus. Measured on deploy #25: 13 of 988 suburb
     * pages exceed 12, putting 49 gyms past the cut; 42 were reachable via a
     * style page and 7 were reachable by no anchor at all. The same cap also
     * left 34 urls named in those pages' JSON-LD ItemList with no rendered link
     * to match — the data-vs-page mismatch M10 was withheld over.
     *
     * The caller passes the suburb's own gym count, so the first page covers the
     * whole (bounded, ≤20) list. It is NOT an "uncapped" flag: widening the
     * radius on a suburb page can still return hundreds, and those still paginate.
     *
     * ⚠️ Floored at GYMS_PER_PAGE below, so a caller passing 0 or a stale small
     * count can never render FEWER gyms than before this option existed.
     */
    initialVisibleCount?: number;
}

export function useDirectoryResults({
    allGyms,
    filters,
    searchCenter,
    userLocation,
    sortBy,
    mapCenter,
    initialVisibleCount
}: UseDirectoryResultsOptions) {
    // The floor is load-bearing, not defensive dressing: it guarantees this option
    // can only ever ADD rows to the first page, so no caller mistake can regress
    // the corpus back into the unreachable-gym state this was built to end.
    const firstPageSize = Math.max(initialVisibleCount ?? GYMS_PER_PAGE, GYMS_PER_PAGE);
    const [visibleCount, setVisibleCount] = useState(firstPageSize);
    const [filteredGyms, setFilteredGyms] = useState<Gym[]>(allGyms);
    // DL-11: true once the Web Worker has produced filteredGyms for the CURRENT
    // allGyms+filters (or there is nothing to filter). Lets the page distinguish
    // "worker still filtering" from "genuinely zero matches", so the "No Gyms
    // Found" empty state can't flash during the async compute window.
    const [resultsSettled, setResultsSettled] = useState(true);

    // DL-10: the Web Worker does the heavy FILTER only (full-corpus scan + radius
    // haversine). It re-runs only when the filter inputs change — NOT on a
    // sort-only change — so switching sort no longer tears down the worker or
    // structured-clones the whole gym array again.
    useEffect(() => {
        if (allGyms.length === 0) {
            setFilteredGyms([]);
            setResultsSettled(true);
            return;
        }

        setResultsSettled(false);
        let settled = false;
        const settle = () => { settled = true; setResultsSettled(true); };

        // Schedule the failsafe BEFORE constructing the worker: if the worker never
        // reports back — a synchronous constructor throw (no Worker support /
        // CSP-blocked worker-src), an async onerror, or a hang — settle anyway with
        // the unfiltered set, so gating the render on resultsSettled can never leave
        // the page stuck on skeletons.
        const failsafe = setTimeout(() => {
            if (!settled) { setFilteredGyms(allGyms); settle(); }
        }, 4000);

        let worker: Worker | null = null;
        try {
            worker = new Worker(new URL('../workers/directory.worker.ts', import.meta.url), { type: 'module' });
            worker.onmessage = (e: MessageEvent<any>) => {
                if (e.data.type === 'PROCESS_COMPLETE') {
                    setFilteredGyms(e.data.payload.gyms);
                    settle();
                } else if (e.data.type === 'PROCESS_ERROR') {
                    console.error('Directory worker error:', e.data.payload);
                    setFilteredGyms([]);
                    settle();
                }
            };
            worker.onerror = (err) => {
                console.error('Directory worker failed to run:', err.message);
                setFilteredGyms(allGyms); // fall back to the unfiltered set, not blank
                settle();
            };
            worker.postMessage({
                type: 'START_PROCESS',
                payload: { allGyms, filters, searchCenter }
            });
        } catch (err) {
            // e.g. Worker undefined or blocked — degrade gracefully to unfiltered.
            console.error('Directory worker could not start:', err);
            setFilteredGyms(allGyms);
            settle();
        }

        return () => { clearTimeout(failsafe); worker?.terminate(); };
    }, [allGyms, filters, searchCenter]);

    // DL-10: sort the filtered set client-side (cheap for a few hundred gyms) so a
    // sort-only change is a pure in-memory re-order with no worker round-trip.
    //
    // ⚠️⚠️ UI-129 (loading spec RULED D2 / A2) — THE LAST-RESORT TIEBREAK BELOW IS LOAD-
    // BEARING AND MUST STAY ON ALL THREE COMPARATORS. Without it a tie falls through to
    // `Array.prototype.sort`'s stability, i.e. to the INPUT order — and the input order is
    // precisely what the quiet reconcile replaces wholesale
    // (`useDirectorySearch.ts` setAllGyms). So tied cards swapped places under the reader
    // seconds after a page settled, which A2 forbids.
    //
    // ⚠️ THE TIES ARE NOT RARE, which is why this is not decoration:
    //   · distance — every gym with a non-finite lat/lng scores `Infinity`, so ALL
    //     coordinate-less gyms are mutually tied in one group;
    //   · rating / reviews — two gyms sharing a rating AND a review count tie outright,
    //     which is far more common than a distance tie. All three are user-selectable
    //     from the Sort control, so all three need it.
    //
    // ⚠️ `id` and not `name`: ids are unique and immutable, so the order can never move
    // again. The operator was offered alphabetical-by-name and declined it knowing the
    // trade — within a tied group this order reads as arbitrary, which is the accepted
    // cost of it never changing. Do NOT "improve" it to a human-meaningful key without
    // re-taking that decision: a renamed gym would start moving between reconciles again.
    const byId = (a: Gym, b: Gym) => {
        const ka = a.stableId || a.id;
        const kb = b.stableId || b.id;
        return ka < kb ? -1 : ka > kb ? 1 : 0;
    };

    const sortedGyms = useMemo(() => {
        if (filteredGyms.length === 0) return [];
        let originLat = searchCenter?.lat ?? mapCenter[0];
        let originLng = searchCenter?.lng ?? mapCenter[1];
        if (!searchCenter && userLocation) {
            originLat = userLocation.lat;
            originLng = userLocation.lng;
        }
        const arr = [...filteredGyms];
        if (sortBy === 'distance') {
            arr.sort((a, b) => {
                const dA = Number.isFinite(a.lat) && Number.isFinite(a.lng)
                    ? calculateHaversineDistance(originLat, originLng, a.lat, a.lng) : Infinity;
                const dB = Number.isFinite(b.lat) && Number.isFinite(b.lng)
                    ? calculateHaversineDistance(originLat, originLng, b.lat, b.lng) : Infinity;
                return dA - dB || byId(a, b);
            });
        } else if (sortBy === 'rating') {
            arr.sort((a, b) => (b.google_rating ?? -1) - (a.google_rating ?? -1) || (b.google_user_ratings_total ?? 0) - (a.google_user_ratings_total ?? 0) || byId(a, b));
        } else if (sortBy === 'reviews') {
            arr.sort((a, b) => (b.google_user_ratings_total ?? 0) - (a.google_user_ratings_total ?? 0) || (b.google_rating ?? 0) - (a.google_rating ?? 0) || byId(a, b));
        }
        return arr;
    }, [filteredGyms, sortBy, searchCenter, userLocation, mapCenter]);

    // 4. Paginate
    const paginatedGyms = useMemo(() => sortedGyms.slice(0, visibleCount), [sortedGyms, visibleCount]);

    // Reset pagination on search change.
    // ⚠️ UI-72: this resets to `firstPageSize`, NOT to GYMS_PER_PAGE. It ran on every
    // `allGyms` change, so a suburb page that prerendered its full list would have
    // collapsed back to 12 the moment the worker settled — the fix would have been
    // visible in the built html and gone a tick after hydration, which is the worst
    // shape available: correct to a crawler, broken to a person, and green in a test
    // that only reads the artefact.
    useEffect(() => {
        setVisibleCount(firstPageSize);
    }, [allGyms, firstPageSize]);

    const handleLoadMore = () => setVisibleCount(prev => prev + GYMS_PER_PAGE);

    const showGym = useCallback((gymId: string) => {
        const index = sortedGyms.findIndex(g => g.id === gymId);
        if (index >= 0 && index >= visibleCount) {
            // Calculate how many "pages" we need to show this gym
            const neededPages = Math.ceil((index + 1) / GYMS_PER_PAGE);
            setVisibleCount(neededPages * GYMS_PER_PAGE);
        }
    }, [sortedGyms, visibleCount]);

    return {
        sortedGyms,
        paginatedGyms,
        handleLoadMore,
        showGym,
        hasMore: sortedGyms.length > paginatedGyms.length,
        resultsSettled
    };
}
